import React, { useEffect, useRef } from "react";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../store/appStore";
import styles from "./NotificationModal.module.css";

const SuccessIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const ErrorIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="15" y1="9" x2="9" y2="15"></line>
    <line x1="9" y1="9" x2="15" y2="15"></line>
  </svg>
);

const ValidationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

// Component to render validation error messages
const ErrorDisplay = ({ errors, displayMessages }) => {
  // Prefer pre-computed display messages (new path)
  if (
    displayMessages &&
    typeof displayMessages === "object" &&
    Object.keys(displayMessages).length > 0
  ) {
    return (
      <>
        {Object.entries(displayMessages).map(([section, messages]) => (
          <div key={section} className={styles.errorSection}>
            <div className={styles.errorSectionTitle}>{section}</div>
            <ul className={styles.errorList}>
              {messages.map((msg, i) => (
                <li key={i} className={styles.errorItem}>
                  {msg}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </>
    );
  }

  // Fallback for backend errors or legacy callers without displayMessages
  if (errors) {
    // If errors is a simple string
    if (typeof errors === "string") {
      return (
        <div className={styles.errorSummary}>
          <p>{errors}</p>
        </div>
      );
    }
    // Generic fallback
    return (
      <div className={styles.errorSummary}>
        <p>There are validation errors in the form.</p>
        <p className={styles.errorNote}>
          Please check all tabs for highlighted fields that need correction.
        </p>
      </div>
    );
  }

  return null;
};

const NotificationModal = () => {
  const { isActive, type, title, message, errors, displayMessages } =
    useAppStore((s) => s.notification, shallow);
  // Actions
  const hideNotification = useAppStore(
    (s) => s.notificationActions.hideNotification
  );
  const modalRef = useRef(null);

  const handleClose = () => {
    hideNotification();
  };

  // Close when clicking outside the modal
  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose();
      }
    }

    if (isActive) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isActive, handleClose]);

  // Close on escape key
  useEffect(() => {
    function handleEscapeKey(event) {
      if (event.key === "Escape") {
        handleClose();
      }
    }

    if (isActive) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [isActive, handleClose]);

  if (!isActive) return null;

  let icon;
  switch (type) {
    case "success":
      icon = <SuccessIcon />;
      break;
    case "error":
      icon = <ErrorIcon />;
      break;
    case "validation":
      icon = <ValidationIcon />;
      break;
    default:
      icon = <SuccessIcon />;
  }

  return (
    <div className={styles.notificationOverlay}>
      <div
        ref={modalRef}
        className={`${styles.notificationContainer} ${styles[type]}`}
      >
        <button
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close notification"
        >
          ×
        </button>

        <div className={styles.notificationHeader}>
          <div className={styles.iconContainer}>{icon}</div>
          <h2 className={styles.notificationTitle}>{title}</h2>
        </div>

        {message && <div className={styles.notificationMessage}>{message}</div>}

        {type === "validation" && (errors || displayMessages) && (
          <div className={styles.errorsContainer}>
            <ErrorDisplay errors={errors} displayMessages={displayMessages} />
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationModal;
